import React from 'react';
import { Box, Card, CardContent, Chip, Divider, Grid, Stack, Typography } from '@mui/material';

const HistoryDetail = ({ entry }) => {
  if (!entry) {
    return (
      <Box py={4} textAlign="center">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const formatDate = dateString => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        month: 'long',
        second: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getActionTypeDisplay = type => {
    switch (type) {
      case 'E':
        return { color: 'success', label: 'Entry' };
      case 'S':
        return { color: 'info', label: 'System' };
      default:
        return { color: 'default', label: type };
    }
  };

  const actionType = getActionTypeDisplay(entry.type);

  return (
    <Box>
      {/* Header */}
      <Stack alignItems="center" direction="row" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Entry #{entry.id}</Typography>
        <Chip color={actionType.color} label={actionType.label} variant="filled" />
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* Basic Information */}
      <Grid container mb={3} spacing={3}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography gutterBottom variant="h6">
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="subtitle2">
                      Date & Time
                    </Typography>
                    <Typography variant="body1">{formatDate(entry.date)}</Typography>
                  </Stack>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="subtitle2">
                      Event Type
                    </Typography>
                    <Typography variant="body1">{actionType.label}</Typography>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Information */}
      {entry.user && (
        <Grid container mb={3} spacing={3}>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography gutterBottom variant="h6">
                  User Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item sm={6} xs={12}>
                    <Stack spacing={1}>
                      <Typography color="text.secondary" variant="subtitle2">
                        Name
                      </Typography>
                      <Typography variant="body1">
                        {entry.user.firstName} {entry.user.lastName}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Stack spacing={1}>
                      <Typography color="text.secondary" variant="subtitle2">
                        Email
                      </Typography>
                      <Typography variant="body1">{entry.user.email}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <Typography color="text.secondary" variant="subtitle2">
                        User ID
                      </Typography>
                      <Typography sx={{ fontFamily: 'monospace' }} variant="body1">
                        {entry.userId}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Customer Information */}
      {entry.customer && (
        <Grid container mb={3} spacing={3}>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography gutterBottom variant="h6">
                  Property Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item sm={6} xs={12}>
                    <Stack spacing={1}>
                      <Typography color="text.secondary" variant="subtitle2">
                        Property Name
                      </Typography>
                      <Typography variant="body1">{entry.customer.name}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Stack spacing={1}>
                      <Typography color="text.secondary" variant="subtitle2">
                        Customer ID
                      </Typography>
                      <Typography sx={{ fontFamily: 'monospace' }} variant="body1">
                        {entry.customerId}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Access Point Information */}
      <Grid container spacing={3}>
        <Grid item md={6} xs={12}>
          {entry.bridge && (
            <Card variant="outlined">
              <CardContent>
                <Typography gutterBottom variant="h6">
                  Bridge Information
                </Typography>
                <Stack spacing={2}>
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="subtitle2">
                      Bridge Name
                    </Typography>
                    <Typography variant="body1">{entry.bridge.name}</Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="subtitle2">
                      Serial Number
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace' }} variant="body1">
                      {entry.bridge.serial_number}
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="subtitle2">
                      Bridge ID
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace' }} variant="body1">
                      {entry.bridgeId}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item md={6} xs={12}>
          {entry.gate && (
            <Card variant="outlined">
              <CardContent>
                <Typography gutterBottom variant="h6">
                  Gate Information
                </Typography>
                <Stack spacing={2}>
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="subtitle2">
                      Gate Name
                    </Typography>
                    <Typography variant="body1">{entry.gate.name}</Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="subtitle2">
                      Gate Type
                    </Typography>
                    <Chip label={entry.gate.type} size="small" variant="outlined" />
                  </Stack>
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="subtitle2">
                      Gate ID
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace' }} variant="body1">
                      {entry.gateId}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Technical Details */}
      <Box mt={3}>
        <Card sx={{ bgcolor: 'grey.50' }} variant="outlined">
          <CardContent>
            <Typography gutterBottom variant="h6">
              Technical Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item sm={4} xs={12}>
                <Stack spacing={1}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Entry ID
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace' }} variant="body1">
                    {entry.id}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item sm={4} xs={12}>
                <Stack spacing={1}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Event Type Code
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace' }} variant="body1">
                    {entry.type}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item sm={4} xs={12}>
                <Stack spacing={1}>
                  <Typography color="text.secondary" variant="subtitle2">
                    Raw Date
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace' }} variant="body1">
                    {entry.date}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default HistoryDetail;
